import { useState, useEffect } from 'react';
import { platform } from '../lib/api';

/**
 * Custom hook to fetch real-time platform statistics
 * Pulls data directly from Supabase database
 */
export const usePlatformStats = () => {
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalLayers: 0,
    totalImages: 0,
    totalRules: 0,
    totalPaths: 0,
    totalNFTsGenerated: 0,
    totalPossibleCombinations: 0,
    uniqueUsers: 0,
    avgCollectionSize: 10000,
    avgLayersPerProject: 0,
    avgImagesPerLayer: 0,
    recentProjects: 0,
    blockchainDistribution: {},
    loading: true,
    error: null,
  });

  useEffect(() => {
    fetchPlatformStats();
  }, []);

  const fetchPlatformStats = async () => {
    try {
      setStats(prev => ({ ...prev, loading: true, error: null }));

      // Fetch all platform stats from server (single endpoint)
      const result = await platform.getStats();
      const s = result.stats;

      if (!s) {
        console.warn('No global stats found, using defaults');
        setStats(prev => ({
          ...prev,
          loading: false,
          error: null,
        }));
        return;
      }

      setStats({
        totalProjects: s.totalProjects || 0,
        totalLayers: s.totalLayers || 0,
        totalImages: s.totalImages || 0,
        totalRules: s.totalRules || 0,
        totalPaths: s.totalPaths || 0,
        totalNFTsGenerated: s.totalNFTsGenerated || 0,
        totalPossibleCombinations: s.totalPossibleCombinations || 0,
        uniqueUsers: s.uniqueUsers || 0,
        avgCollectionSize: s.avgCollectionSize || 10000,
        avgLayersPerProject: s.avgLayersPerProject || 0,
        avgImagesPerLayer: s.avgImagesPerLayer || 0,
        recentProjects: s.recentProjects || 0,
        blockchainDistribution: s.blockchainDistribution || {},
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching platform stats:', error);
      setStats(prev => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  return { ...stats, refetch: fetchPlatformStats };
};

/**
 * Custom hook to fetch featured/public collections
 */
export const useFeaturedCollections = (limit = 12) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFeaturedCollections();
  }, [limit]);

  const fetchFeaturedCollections = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await platform.getFeaturedCollections(limit);
      const data = result.data || [];

      // Enhance with calculated stats
      const enhancedCollections = data.map(collection => {
        const layerCount = collection.forge_layers?.length || 0;
        const imageCount = collection.forge_layers?.reduce((sum, layer) => {
          return sum + (layer.forge_images?.length || 0);
        }, 0) || 0;

        const possibleCombinations = collection.forge_layers?.reduce((product, layer) => {
          const count = layer.forge_images?.length || 1;
          return product * count;
        }, 1) || 0;

        return {
          id: collection.id,
          name: collection.name,
          description: collection.description,
          collectionSize: collection.collection_size,
          blockchain: collection.blockchain,
          imageUrl: collection.image_url,
          category: collection.category,
          tags: collection.tags,
          createdAt: collection.created_at,
          stats: {
            layers: layerCount,
            images: imageCount,
            possibleCombinations,
          },
        };
      }) || [];

      setCollections(enhancedCollections);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching featured collections:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return { collections, loading, error, refetch: fetchFeaturedCollections };
};
